import { IPersonalDetails } from 'API';
import {
  textTooLong,
  onlyNumbers,
  onlyNumbersMessage,
  emailMessage,
  requiredMessage,
  lettersOrNumbersNoSpecialChars,
  lettersOrNumbersNoSpecialCharsMessage,
  englishLettersAndNumbers,
  englishLettersAndNumbersMessage,
  englishLettersOnly,
  englishLettersWithSpaceAndHyphen,
  emailRegexStrict,
  minDate,
  dateMessage,
} from 'Regex';
import { store } from 'Store';
import * as yup from 'yup';
import { ISRAEL_ID } from '../FirstSection';
import { hmoStillInMilitaryServiceId } from '../FourthSection';

// Not in use yet not enough time to implement schema
export const personalDetailsSchema: yup.SchemaOf<IPersonalDetails> = yup
  .object()
  .shape({
    contactInfoSection: yup.object({
      phoneNumber: yup
        .string()
        .matches(onlyNumbers, onlyNumbersMessage)
        .max(7, textTooLong(7))
        .required(requiredMessage)
        .nullable(),
      phonePrefixRelationId: yup.string().required(requiredMessage).nullable(),
      extraPhoneNumber: yup
        .string()
        .matches(onlyNumbers, onlyNumbersMessage)
        .when('hasNoExtraPhoneNumber', {
          is: (val: boolean) => val === false,
          then: yup.string().required(requiredMessage).max(7, textTooLong(7)),
          otherwise: yup.string().notRequired().nullable(),
        }),
      extraPhonePrefixRelationId: yup.string().when('hasNoExtraPhoneNumber', {
        is: (val: boolean) => val === false,
        then: yup.string().required(requiredMessage).nullable(),
        otherwise: yup.string().notRequired().nullable(),
      }),
      hasNoExtraPhoneNumber: yup.boolean().when('isRecognized', {
        is: () => store.getState().generalContent.formInfo.isRecognized,
        then: yup.boolean().notRequired().nullable(),
        otherwise: yup.boolean().notRequired().nullable(),
      }),
      mail: yup.string().when('isRecognized', {
        is: () => store.getState().generalContent.formInfo.isRecognized,
        then: yup.string().notRequired().nullable(),
        otherwise: yup
          .string()
          .when('hasNoMail', {
            is: (hasNoMail: boolean) => hasNoMail === false,
            then: yup
              .string()
              .required(requiredMessage)
              .matches(emailRegexStrict, emailMessage)
              .max(50, textTooLong(50)),
            otherwise: yup.string().notRequired().nullable(),
          })
          .nullable(),
      }),

      hasNoMail: yup.boolean().when('isRecognized', {
        is: () => store.getState().generalContent.formInfo.isRecognized,
        then: yup.boolean().notRequired().nullable(),
        otherwise: yup.boolean().notRequired().nullable(),
      }),
    }),
    hmoSection: yup.object({
      hasNoDoctorName: yup.boolean().notRequired().nullable(),
      hmoRelationId: yup.number().required(requiredMessage).nullable(),
      doctorsNameAndSpecialization: yup
        .string()
        .notRequired()
        .nullable()
        .when(['hasNoDoctorName', 'hmoRelationId'], {
          is: (hasNoDoctorName: boolean, hmoRelationId: number) =>
            hasNoDoctorName === false &&
            hmoRelationId !== hmoStillInMilitaryServiceId,
          then: yup
            .string()
            .required(requiredMessage)
            .matches(
              lettersOrNumbersNoSpecialChars,
              lettersOrNumbersNoSpecialCharsMessage
            ),
          otherwise: yup.string().notRequired().nullable(),
        }),
    }),
    mailBoxSection: yup
      .object({
        isMailBox: yup.boolean().notRequired().nullable(),
        townId: yup
          .string()
          .nullable()
          .test({
            name: 'isRequired',
            message: requiredMessage,
            test: (value, context) => {
              if (
                store.getState().personalDetails.generalObj
                  .isDifferentMailAddress === false
              ) {
                return true;
              } else {
                if (!value) {
                  return false;
                }
                return true;
              }
            },
          }),
        apartmentNumber: yup
          .string()
          .notRequired()
          .nullable()
          .matches(onlyNumbers, onlyNumbersMessage),
        street: yup
          .string()
          .test({
            name: 'isRequired',
            message: requiredMessage,
            test: (value, context) => {
              if (
                store.getState().personalDetails.generalObj
                  .isDifferentMailAddress === false ||
                store.getState().personalDetails.generalObj.isAddressTab ===
                  false
              ) {
                return true;
              } else {
                if (!value) {
                  return false;
                }
                return true;
              }
            },
          })
          .nullable()
          .matches(
            lettersOrNumbersNoSpecialChars,
            lettersOrNumbersNoSpecialCharsMessage
          ),
        mailHouseNumber: yup
          .string()
          .test({
            name: 'isRequired',
            message: requiredMessage,
            test: (value, context) => {
              if (
                store.getState().personalDetails.generalObj
                  .isDifferentMailAddress === false ||
                store.getState().personalDetails.generalObj.isAddressTab ===
                  false
              ) {
                return true;
              } else {
                if (!value) {
                  return false;
                }
                return true;
              }
            },
          })
          .nullable()
          .notRequired()
          .matches(
            lettersOrNumbersNoSpecialChars,
            lettersOrNumbersNoSpecialCharsMessage
          ),
        zipCode: yup
          .string()
          .test({
            name: 'isRequired',
            message: requiredMessage,
            test: (value, context) => {
              if (
                store.getState().generalContent.formInfo.isRecognized ||
                store.getState().personalDetails.generalObj
                  .isDifferentMailAddress === false
              ) {
                return true;
              } else {
                if (!value) {
                  return false;
                }
                return true;
              }
            },
          })
          .test({
            name: 'length',
            message: 'חייב להכיל 7 ספרות',
            test: (value, context) => {
              if (
                store.getState().personalDetails.generalObj
                  .isDifferentMailAddress === false
              ) {
                return true;
              }
              if (value?.length === 7) {
                return true;
              }
              return false;
            },
          })
          .nullable()
          .matches(onlyNumbers, onlyNumbersMessage),
        po: yup
          .string()
          .notRequired()
          .nullable()
          .matches(onlyNumbers, onlyNumbersMessage),
      })
      .notRequired(),
    personalInfoSection: yup.object({
      birthDate: yup
        .string()
        .required(requiredMessage)
        .test({
          name: 'isValidDate',
          message: dateMessage,
          test: (value) => {
            if (!value) return true;
            const d = new Date(value);
            if (isNaN(d.getTime())) return false;
            if (d < minDate) return false;
            return true;
          },
        })
        .nullable(),
      birthCountryId: yup
        .number()
        .test({
          name: 'isRequired',
          message: requiredMessage,
          test: (val) => {
            if (store.getState().generalContent.formInfo.isRecognized || val) {
              return true;
            }
            return false;
          },
        })
        .nullable(),
      immigrationYear: yup.string().when('birthCountryId', {
        is: (val: any) => val !== ISRAEL_ID,
        then: yup.string().required(requiredMessage).nullable(),
        otherwise: yup.string().notRequired().nullable(),
      }),
      genderId: yup.number().required(requiredMessage).nullable(),
      maritalStatusId: yup
        .number()
        .when('isRecognized', {
          is: () => store.getState().generalContent.formInfo.isRecognized,
          then: yup.number().nullable(),
          otherwise: yup.number().required(requiredMessage).nullable(),
        })
        .nullable(),

      firstName: yup
        .string()
        .required(requiredMessage)
        .max(20, textTooLong(20))
        .matches(
          lettersOrNumbersNoSpecialChars,
          lettersOrNumbersNoSpecialCharsMessage
        )
        .nullable(),
      lastName: yup
        .string()
        .required(requiredMessage)
        .max(30, textTooLong(30))
        .matches(
          lettersOrNumbersNoSpecialChars,
          lettersOrNumbersNoSpecialCharsMessage
        )
        .nullable(),
      firstNameEnglish: yup
        .string()
        .required(requiredMessage)
        .max(30, textTooLong(30))
        .matches(englishLettersWithSpaceAndHyphen, englishLettersOnly)
        .test({
          name: 'isRequired',
          message: requiredMessage,
          test: (val) => {
            if (val && englishLettersWithSpaceAndHyphen.test(val)) return true;
            return false;
          },
        })
        .nullable(),
      lastNameEnglish: yup
        .string()
        .required(requiredMessage)
        .max(30, textTooLong(30))
        .matches(englishLettersWithSpaceAndHyphen, englishLettersOnly)
        .test({
          name: 'isRequired',
          message: requiredMessage,
          test: (val) => {
            if (val && englishLettersWithSpaceAndHyphen.test(val)) return true;
            return false;
          },
        })
        .nullable(),
      israeliId: yup.string().required(requiredMessage).nullable(),
    }),
    previousHMOSection: yup.array().of(
      yup.object({
        hmoRelationId: yup.number().required(requiredMessage).nullable(),
        hospitalizationEndDate: yup
          .string()
          .required(requiredMessage)
          .nullable(),
      })
    ),
    residentialAddressSection: yup.object({
      israeliAddressRelation: yup.object({
        israeliTownId: yup
          .string()
          .test({
            name: 'isRequired',
            message: requiredMessage,
            test: (val) => {
              if (val) {
                return true;
              }
              return false;
            },
          })
          .nullable(),
        apartmentNumber: yup
          .string()
          .notRequired()
          .nullable()
          .matches(onlyNumbers, onlyNumbersMessage),
        street: yup
          .string()
          .test({
            name: 'isRequired',
            message: requiredMessage,
            test: (val) => {
              if (val !== '') {
                return true;
              }
              return false;
            },
          })
          .nullable()
          .matches(
            lettersOrNumbersNoSpecialChars,
            lettersOrNumbersNoSpecialCharsMessage
          ),
        homeNumHouse: yup
          .string()
          .test({
            name: 'isRequired',
            message: requiredMessage,
            test: (val) => {
              if (val !== '') {
                return true;
              }
              return false;
            },
          })
          .nullable()
          .matches(
            lettersOrNumbersNoSpecialChars,
            lettersOrNumbersNoSpecialCharsMessage
          ),
        israeliZipCode: yup
          .string()
          .when('isRecognized', {
            is: () => store.getState().generalContent.formInfo.isRecognized,
            then: yup.string().nullable(),
            otherwise: yup
              .string()
              .test({
                name: 'isRequired',
                message: requiredMessage,
                test: (val) => {
                  if (val !== '') {
                    return true;
                  }
                  return false;
                },
              })
              .nullable()
              .min(7, 'חייב להכיל 7 ספרות')
              .matches(onlyNumbers, onlyNumbersMessage),
          })
          .nullable(),
      }),
      isAbroadResidentialAddress: yup.boolean().notRequired().nullable(),
      abroadAddressRelation: yup.object().when('isAbroadResidentialAddress', {
        is: (val: boolean) => val === true,
        then: yup.object({
          countryId: yup.string().required(requiredMessage).nullable(),
          street: yup
            .string()
            .required(requiredMessage)
            .nullable()
            .matches(
              lettersOrNumbersNoSpecialChars,
              lettersOrNumbersNoSpecialCharsMessage
            ),
          homeNumHouse: yup
            .string()
            .required(requiredMessage)
            .nullable()
            .matches(onlyNumbers),
          abroadTown: yup
            .string()
            .required(requiredMessage)
            .nullable()
            .matches(
              lettersOrNumbersNoSpecialChars,
              lettersOrNumbersNoSpecialCharsMessage
            ),
          abroadZipCode: yup
            .string()
            .required(requiredMessage)
            .nullable()
            .matches(englishLettersAndNumbers, englishLettersAndNumbersMessage),
        }),
        otherwise: yup.object().nullable().notRequired(),
      }),
    }),
  });
